import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, X, FilePdf } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import SkinfoldsSection from './SkinfoldsSection';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FIELDS = [
  { k: 'peito', l: 'Peito', x: 100, y: 70, color: '#657161' },
  { k: 'cintura', l: 'Cintura', x: 100, y: 105, color: '#7A8B76' },
  { k: 'abdomen', l: 'Abdómen', x: 100, y: 120, color: '#8FA487' },
  { k: 'anca', l: 'Anca', x: 100, y: 145, color: '#B8C9A6' },
  { k: 'braco_direito', l: 'Braço dir.', x: 60, y: 90, color: '#D7BD7B' },
  { k: 'braco_esquerdo', l: 'Braço esq.', x: 140, y: 90, color: '#E3C887' },
  { k: 'coxa_direita', l: 'Coxa dir.', x: 85, y: 180, color: '#9D5B6E' },
  { k: 'coxa_esquerda', l: 'Coxa esq.', x: 115, y: 180, color: '#B57184' },
  { k: 'gemeo_direito', l: 'Gémeo dir.', x: 85, y: 230, color: '#7A8FB0' },
  { k: 'gemeo_esquerdo', l: 'Gémeo esq.', x: 115, y: 230, color: '#90A3C0' },
];

function FemaleSilhouette({ values, onPointClick, readOnly = false }) {
  return (
    <div className="relative mx-auto" style={{ width: 200, height: 290 }} data-testid="silhouette-svg">
      <svg viewBox="0 0 200 290" className="absolute inset-0 w-full h-full">
        {/* Simple female silhouette (head, torso, hips, legs) */}
        <ellipse cx="100" cy="30" rx="14" ry="17" fill="#F0EFE9" stroke="#C4C7BC" strokeWidth="1" />
        <path d="M 80 50 Q 80 55 75 65 L 70 85 Q 65 95 60 105 L 55 95 Q 50 90 45 95 L 50 110 Q 60 115 65 110 L 70 130 Q 75 145 75 160 Q 70 165 70 175 L 78 240 Q 80 270 85 275 L 90 275 Q 92 270 90 240 L 92 175 L 100 175 L 108 175 L 110 240 Q 108 270 110 275 L 115 275 Q 120 270 122 240 L 130 175 Q 130 165 125 160 Q 125 145 130 130 L 135 110 Q 140 115 150 110 L 155 95 Q 150 90 145 95 L 140 105 Q 135 95 130 85 L 125 65 Q 120 55 120 50 Q 110 45 100 45 Q 90 45 80 50 Z"
              fill="#F7F8F6" stroke="#C4C7BC" strokeWidth="1" />
        {/* Click points */}
        {FIELDS.map(f => {
          const v = values?.[f.k];
          const has = typeof v === 'number';
          return (
            <g key={f.k} style={{ cursor: readOnly ? 'default' : 'pointer' }} onClick={() => !readOnly && onPointClick && onPointClick(f.k)}>
              <circle cx={f.x} cy={f.y} r={has ? 7 : 5} fill={has ? f.color : '#FFF'} stroke={f.color} strokeWidth="1.5" />
              {has && <text x={f.x} y={f.y + 2} textAnchor="middle" fontSize="6" fill="#FFF" fontWeight="600">{v}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function PerimetrosTab({ patientId, readOnly = false }) {
  const confirmDelete = useConfirmDelete();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], measurements: {}, notes: '' });

  const isClientView = !patientId; // when no patientId, we're on the client's own page
  const baseUrl = patientId ? `${API}/api/patients/${patientId}/measurements` : `${API}/api/my-measurements`;

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(baseUrl);
      setList(Array.isArray(data) ? data : []);
    } catch {} finally { setLoading(false); }
  }, [baseUrl]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const latest = list[0]; // most recent
  const latestValues = latest?.measurements || {};

  const open = (m) => {
    setEditing(m);
    setForm({
      date: m?.date || new Date().toISOString().split('T')[0],
      measurements: { ...(m?.measurements || {}) },
      notes: m?.notes || '',
    });
    setShowForm(true);
  };
  const save = async () => {
    try {
      const payload = { date: form.date, measurements: form.measurements, notes: form.notes };
      if (editing?.id) await axios.put(`${baseUrl}/${editing.id}`, payload);
      else await axios.post(baseUrl, payload);
      toast.success(editing ? 'Atualizado' : 'Medição registada');
      setShowForm(false); setEditing(null); setActiveField(null);
      setForm({ date: new Date().toISOString().split('T')[0], measurements: {}, notes: '' });
      fetchAll();
    } catch { toast.error('Erro a guardar'); }
  };
  const remove = async (m) => {
    const ok = await confirmDelete({ description: `Vai apagar a medição de perímetros de ${m.date}.` });
    if (!ok) return;
    try { await axios.delete(`${baseUrl}/${m.id}`); toast.success('Eliminada'); fetchAll(); } catch { toast.error('Erro'); }
  };

  // Chart data: each row = date, plus one numeric column per field
  const chartData = useMemo(() => {
    const sorted = [...list].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    return sorted.map(r => {
      const row = { date: r.date };
      FIELDS.forEach(f => { row[f.k] = r.measurements?.[f.k]; });
      return row;
    });
  }, [list]);

  const usedFields = useMemo(() => FIELDS.filter(f => list.some(r => typeof r.measurements?.[f.k] === 'number')), [list]);

  if (loading) return <div className="text-center py-8 text-sm text-brand-muted">A carregar perímetros…</div>;

  return (
    <div className="space-y-5" data-testid="perimetros-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-brand-muted">Perímetros corporais</p>
          {latest && <p className="text-[10px] text-brand-placeholder">Última medição: {fmtDatePt(latest.date)}</p>}
        </div>
        {!readOnly && (
          <button onClick={() => open(null)} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2" data-testid="add-measurement-btn">
            <Plus size={14} /> Nova medição
          </button>
        )}
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <FemaleSilhouette
          values={latestValues}
          readOnly={readOnly}
          onPointClick={(k) => { open(latest || null); setActiveField(k); }}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          {FIELDS.map(f => (
            <div key={f.k} className="text-[11px] flex items-center justify-between bg-brand-bg/30 rounded-lg px-2 py-1">
              <span className="text-brand-muted">{f.l}</span>
              <span className="font-medium" style={{ color: f.color }}>{latestValues[f.k] != null ? `${latestValues[f.k]} cm` : '-'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      {chartData.length >= 2 && usedFields.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="perimeters-chart">
          <p className="text-xs font-medium text-brand-text mb-2">Evolução</p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {usedFields.map(f => (
                <Line key={f.k} type="monotone" dataKey={f.k} stroke={f.color} strokeWidth={2} dot={{ r: 3 }} name={f.l} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Pregas cutâneas + composição corporal automática (só lado nutricionista/admin) */}
      {patientId && !readOnly && <SkinfoldsSection patientId={patientId} />}

      {/* Cliente: acesso ao PDF de avaliação antropométrica (pregas registadas pela nutri) */}
      {isClientView && (
        <button onClick={() => window.open(`${API}/api/my-skinfolds/pdf`, '_blank')} className="w-full bg-white border border-brand-border rounded-2xl p-3 text-sm text-brand-green flex items-center justify-center gap-2" data-testid="my-skinfolds-pdf-btn">
          <FilePdf size={14} /> Descarregar avaliação antropométrica (PDF)
        </button>
      )}

      {/* History table */}
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <p className="text-xs font-medium text-brand-text mb-2">Histórico ({list.length})</p>
        {list.length === 0 ? (
          <p className="text-xs text-brand-placeholder italic text-center py-4">Sem medições registadas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-brand-muted border-b border-brand-border">
                  <th className="py-1.5 pr-2">Data</th>
                  {FIELDS.map(f => <th key={f.k} className="py-1.5 px-1 text-right">{f.l}</th>)}
                  <th className="py-1.5 pl-2">Notas</th>
                  {!readOnly && <th></th>}
                </tr>
              </thead>
              <tbody>
                {list.map(m => {
                  // T2 — Fase 5: distinguir origem (paciente vs nutricionista)
                  const isFromPatient = m.origin === 'paciente';
                  const canEdit = !readOnly && (!isClientView || isFromPatient);
                  return (
                  <tr key={m.id} className="border-b border-brand-border/50 last:border-0" data-testid={`meas-row-${m.id}`}>
                    <td className="py-1.5 pr-2 font-medium">
                      {m.date}
                      {/* Only show badge in nutri view (with patientId) to distinguish records */}
                      {patientId && isFromPatient && <span className="ml-1.5 text-[8px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full uppercase tracking-wider font-medium" data-testid={`meas-origin-paciente-${m.id}`}>paciente</span>}
                      {patientId && m.origin === 'nutricionista' && <span className="ml-1.5 text-[8px] bg-brand-bg text-brand-muted px-1.5 py-0.5 rounded-full uppercase tracking-wider font-medium" data-testid={`meas-origin-nutri-${m.id}`}>nutri</span>}
                    </td>
                    {FIELDS.map(f => (
                      <td key={f.k} className="py-1.5 px-1 text-right text-brand-muted">{m.measurements?.[f.k] ?? '-'}</td>
                    ))}
                    <td className="py-1.5 pl-2 text-brand-placeholder max-w-[120px] truncate">{m.notes || ''}</td>
                    {!readOnly && (
                      <td className="py-1.5 pl-2 flex gap-1">
                        {canEdit && (
                          <>
                            <button onClick={() => open(m)} className="text-brand-muted hover:text-brand-green p-1" data-testid={`edit-meas-${m.id}`}><PencilSimple size={12} /></button>
                            <button onClick={() => remove(m)} className="text-brand-muted hover:text-red-500 p-1" data-testid={`del-meas-${m.id}`}><Trash size={12} /></button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); setActiveField(null); } }}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editing ? 'Editar medição' : 'Nova medição'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Data</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="meas-date" />
            </div>
            <FemaleSilhouette values={form.measurements} onPointClick={(k) => setActiveField(k)} />
            <div className="grid grid-cols-2 gap-2">
              {FIELDS.map(f => (
                <div key={f.k}>
                  <Label className="text-[10px] text-brand-muted block">{f.l} (cm)</Label>
                  <Input
                    type="number" step="0.1" min="0"
                    value={form.measurements[f.k] ?? ''}
                    onChange={e => {
                      const v = e.target.value;
                      const m = { ...form.measurements };
                      if (v === '') delete m[f.k]; else m[f.k] = parseFloat(v);
                      setForm({ ...form, measurements: m });
                    }}
                    autoFocus={activeField === f.k}
                    placeholder="-"
                    className="rounded-xl border-brand-border text-sm h-8"
                    data-testid={`meas-input-${f.k}`}
                  />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-xl border-brand-border text-sm" />
            </div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="save-meas-btn">
              {editing ? 'Guardar alterações' : 'Guardar medição'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
